import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { OrgSelection } from "@/components/agents/configuration-server/OrgStructureListView";
import { DecisionMap, type MapItem } from "@/components/agents/configuration-server/stages/DecisionMap";
import {
  effectiveOrgId,
  orgUnitTypeLabelKey,
  type OrgTreeNode,
  type ScopeVerdict,
} from "@/lib/org-structure-tree";

/** Só os pendentes da família: decidido não entra na seleção em lote. */
function pendingSubtree(node: OrgTreeNode): OrgTreeNode[] {
  return [...(node.origin === "manual" && node.scopeVerdict === "pending" ? [node] : []), ...node.children.flatMap(pendingSubtree)];
}

/**
 * Mapa da estrutura organizacional no Gerenciamento: a árvore canônica inteira,
 * pendentes e decididos. `matchKeys` vem dos filtros: o que não bate fica apagado.
 */
export function OrgDecisionMap({
  roots,
  matchKeys,
  selection,
  onRequestVerdict,
  onReturn,
}: {
  roots: OrgTreeNode[];
  matchKeys: ReadonlySet<string> | null;
  selection: OrgSelection;
  onRequestVerdict: (node: OrgTreeNode, verdict: Exclude<ScopeVerdict, "pending">) => void;
  onReturn?: (node: OrgTreeNode) => void;
}) {
  const { t } = useTranslation();
  const selected = selection.selectedKeys;
  const items = useMemo(() => {
    const toItem = (node: OrgTreeNode): MapItem<OrgTreeNode> => ({
      key: node.key,
      kind: "item",
      code: effectiveOrgId(node),
      name: node.name ?? "—",
      type: t(orgUnitTypeLabelKey(node.unitTypeLabel, node.levelName), { defaultValue: node.levelName }),
      status: node.scopeVerdict,
      done: node.migrationStatus === "completed",
      checked: selected.has(node.key),
      locked: node.origin !== "manual",
      sourceLabel: node.origin !== "manual" ? t("From the Diagnostic") : undefined,
      dimmed: matchKeys !== null && !matchKeys.has(node.key),
      data: node,
      children: node.children.map(toItem),
    });
    return roots.map(toItem);
  }, [roots, matchKeys, selected, t]);

  return (
    <DecisionMap
      roots={items}
      onCheck={(node, checked) => selection.onToggle(pendingSubtree(node), checked)}
      onApprove={(node) => onRequestVerdict(node, "approved")}
      onOut={(node) => onRequestVerdict(node, "out_of_scope")}
      onUndo={onReturn}
    />
  );
}
